import { useEffect, useRef, useState } from 'react';
import { FLOOR_BAND, type Point } from '../wanderMath';
import type { PlayletProps } from './types';

const WIN_POPS = 8;
const RISE_PCT_PER_MS = 0.012;

export type BubblesPlayletProps = PlayletProps & { rng?: () => number };

function spawnBubble(rng: () => number, band = FLOOR_BAND): Point {
  return {
    x: band.xMin + rng() * (band.xMax - band.xMin),
    y: band.yMin + rng() * (band.yMax - band.yMin),
  };
}

export function BubblesPlaylet({ onWin, onQuit, rng = Math.random }: BubblesPlayletProps) {
  const rngRef = useRef(rng);
  rngRef.current = rng;
  const [bubble, setBubble] = useState<Point | null>(() => spawnBubble(rng));
  const [pops, setPops] = useState(0);
  const popsRef = useRef(0);
  const quitRef = useRef(false);
  const wonRef = useRef(false);

  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      setBubble((p) => {
        if (!p || wonRef.current || quitRef.current) return p;
        const y = p.y - RISE_PCT_PER_MS * dt;
        if (y < 0) return spawnBubble(rngRef.current);
        return { ...p, y };
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  function pop() {
    if (quitRef.current || wonRef.current) return;
    popsRef.current += 1;
    setPops(popsRef.current);
    if (popsRef.current >= WIN_POPS) {
      wonRef.current = true;
      setBubble(null);
      onWin();
      return;
    }
    setBubble(spawnBubble(rngRef.current));
  }

  function handleQuit() {
    quitRef.current = true;
    onQuit();
  }

  return (
    <div className="habitat-playlet">
      <p className="form-note habitat-playlet-hint">Pop the bubbles</p>
      <span className="chip habitat-playlet-progress">{pops}/{WIN_POPS}</span>
      <button type="button" className="chip habitat-playlet-quit" onClick={handleQuit}>
        Quit
      </button>
      {bubble && (
        <button
          type="button"
          className="habitat-playlet-bubble"
          aria-label="Bubble"
          style={{ left: `${bubble.x}%`, top: `${bubble.y}%` }}
          onClick={pop}
        />
      )}
    </div>
  );
}
